import { css } from '@emotion/react'
import { colorTokens } from '../../styles/tokens'

/**
 * Stat-card styles.
 *
 * These cards are equal-sized slides in the login showcase, on the dark green
 * panel. They follow the marketing homepage (September 2026): white cards with
 * a 1.25rem corner and the frame shadow, no border, headings in Cabinet Grotesk
 * at 800 with tight tracking, and the ink and muted greys for copy. Every tone
 * renders as the same white card: on the dark ground the homepage allows only
 * white, so `tone` is kept for the API and no longer changes the surface.
 */
type Tone = 'light' | 'dark' | 'mint'

const DISPLAY_FONT =
  "var(--font-cabinet-grotesk), 'system-ui', Arial, sans-serif"

const surface = css`
  background: ${colorTokens.white};
  color: ${colorTokens.slate900};
  box-shadow:
    0 32px 64px -24px rgba(20, 39, 27, 0.28),
    0 12px 24px -12px rgba(20, 39, 27, 0.12);
`

const mutedText = css`
  color: ${colorTokens.slate600};
`

export const styles = {
  card: css`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    border-radius: 20px;
    padding: 32px;
    overflow: hidden;
    text-wrap-style: balance;
  `,
  fill: css`
    flex: 1;
    height: 100%;
  `,
  headerRow: css`
    align-items: center;
  `,
  body: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
  `,

  tone: {
    light: surface,
    dark: surface,
    mint: surface,
  } satisfies Record<Tone, ReturnType<typeof css>>,

  dot: css`
    width: 11px;
    height: 11px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${colorTokens.green100};
  `,
  dotTone: {
    light: css``,
    dark: css``,
    mint: css``,
  } satisfies Record<Tone, ReturnType<typeof css>>,

  // The small line over the card, as the vignettes label themselves ("Allocation
  // record"): sentence case in the muted grey, not an uppercase eyebrow.
  kicker: css`
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.4;
  `,
  kickerTone: {
    light: mutedText,
    dark: mutedText,
    mint: mutedText,
  } satisfies Record<Tone, ReturnType<typeof css>>,

  // Focal value — the single large number/word on each card.
  metric: css`
    font-weight: 800;
    font-size: 64px;
    line-height: 0.95;
    letter-spacing: -0.025em;
  `,
  metricSm: css`
    font-weight: 800;
    font-size: 32px;
    line-height: 1.15;
    letter-spacing: -0.025em;
  `,
  metricLabel: css`
    font-size: 1.125rem;
    line-height: 1.5;
    font-weight: 400;
    color: ${colorTokens.slate600};
    text-wrap-style: balance;
  `,
  description: css`
    text-wrap-style: balance;
    font-size: 1rem;
    line-height: 1.6;
  `,
  descriptionTone: {
    light: mutedText,
    dark: mutedText,
    mint: mutedText,
  } satisfies Record<Tone, ReturnType<typeof css>>,

  footer: css`
    margin-top: auto;
    padding-top: 20px;
    border-top: 1px solid ${colorTokens.slate200};
  `,

  subValue: css`
    font-weight: 800;
    font-size: 28px;
    line-height: 1;
    letter-spacing: -0.025em;
  `,
  subLabel: css`
    font-size: 0.875rem;
    font-weight: 400;
    color: ${colorTokens.slate600};
  `,

  // Pills (compliance card): the homepage's status pills.
  pillRow: css`
    flex-wrap: wrap;
  `,
  pillSolid: css`
    background: ${colorTokens.green100};
    border: 1px solid ${colorTokens.green100};
    color: ${colorTokens.white};
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1;
    padding: 0.5em 0.9em;
    border-radius: 999px;
  `,
  pillOutline: css`
    background: ${colorTokens.white};
    color: ${colorTokens.green200};
    border: 1px solid ${colorTokens.green500};
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1;
    padding: 0.5em 0.9em;
    border-radius: 999px;
  `,

  link: css`
    font-weight: 500;
    font-size: 0.875rem;
    white-space: nowrap;
  `,

  // Cabinet Grotesk 800 display treatment — matches the homepage headings and
  // the login form's title (see dashboard/login/styles.ts `headingFont`).
  headingFont: css`
    font-family: ${DISPLAY_FONT} !important;
    font-weight: 800 !important;
  `,

  // Icon box matching the homepage feature cards: a 44px rounded square in the
  // subtle brand tint behind a brand-green icon.
  iconChip: css`
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: #ecf3ec;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 24px;
      height: 24px;
      color: ${colorTokens.green100};
    }
  `,
}
